import { useState, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Typography,
  Box,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material'
import { FileUpload, CheckCircle, Upload } from '@mui/icons-material'
import { adminApi } from '@api/endpoints/admin'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import {
  AdventureImportResult,
  AdventureBundleImportResult,
} from '@api/types/admin'

interface AdventureImportDialogProps {
  open: boolean
  onClose: () => void
  onComplete: () => void
}

export function AdventureImportDialog({ open, onClose, onComplete }: AdventureImportDialogProps) {
  const mobileDialogProps = useMobileDialogProps()
  const [activeTab, setActiveTab] = useState(0)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AdventureImportResult | AdventureBundleImportResult | null>(null)

  // JSON import state
  const [jsonFile, setJsonFile] = useState<File | null>(null)
  const jsonInputRef = useRef<HTMLInputElement>(null)

  // Bundle import state
  const [bundleFile, setBundleFile] = useState<File | null>(null)
  const bundleInputRef = useRef<HTMLInputElement>(null)

  // Options
  const [importAsDraft, setImportAsDraft] = useState(true)
  const [skipTeamMembers, setSkipTeamMembers] = useState(true)
  const [importMediaLibrary, setImportMediaLibrary] = useState(true)
  const [importShareCodes, setImportShareCodes] = useState(true)
  const [regenerateQRCodes, setRegenerateQRCodes] = useState(true)

  const handleJsonFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) setJsonFile(file)
    event.target.value = ''
  }

  const handleBundleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) setBundleFile(file)
    event.target.value = ''
  }

  const handleJsonImport = async () => {
    if (!jsonFile) return

    setError(null)
    setResult(null)
    setImporting(true)

    try {
      const text = await jsonFile.text()
      let parsed: unknown
      try {
        parsed = JSON.parse(text)
      } catch {
        setError('Invalid JSON file')
        setImporting(false)
        return
      }

      const importResult = await adminApi.importAdventure(parsed, {
        importAsDraft,
        skipTeamMembers,
      })
      setResult(importResult)
      if (importResult.success) onComplete()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import adventure')
    } finally {
      setImporting(false)
    }
  }

  const handleBundleImport = async () => {
    if (!bundleFile) return

    setError(null)
    setResult(null)
    setImporting(true)

    try {
      const importResult = await adminApi.importAdventureBundle(bundleFile, {
        importAsDraft,
        skipTeamMembers,
        importMediaLibrary,
        importShareCodes,
        regenerateQRCodes,
      })
      setResult(importResult)
      if (importResult.success) onComplete()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import adventure bundle')
    } finally {
      setImporting(false)
    }
  }

  const handleImport = () => {
    if (activeTab === 0) {
      handleJsonImport()
    } else {
      handleBundleImport()
    }
  }

  const handleClose = () => {
    if (importing) return
    setJsonFile(null)
    setBundleFile(null)
    setError(null)
    setResult(null)
    setActiveTab(0)
    setImportAsDraft(true)
    setSkipTeamMembers(true)
    setImportMediaLibrary(true)
    setImportShareCodes(true)
    setRegenerateQRCodes(true)
    onClose()
  }

  const isBundleResult = (r: AdventureImportResult): r is AdventureBundleImportResult =>
    'mediaFilesImported' in r

  const hasFile = activeTab === 0 ? !!jsonFile : !!bundleFile

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth {...mobileDialogProps}>
      <DialogTitle>Import Adventure</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {result && (
          <Alert
            severity={result.success ? (result.warnings.length > 0 ? 'warning' : 'success') : 'error'}
            icon={result.success ? <CheckCircle /> : undefined}
            sx={{ mb: 2 }}
          >
            {result.success ? (
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  Adventure imported successfully
                </Typography>
                {isBundleResult(result) && (
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {result.mediaFilesImported > 0 && (
                      <Chip label={`${result.mediaFilesImported} media files`} size="small" />
                    )}
                    {result.shareCodesImported > 0 && (
                      <Chip label={`${result.shareCodesImported} share codes`} size="small" />
                    )}
                  </Box>
                )}
                {result.warnings.length > 0 && (
                  <List dense disablePadding sx={{ mt: 1 }}>
                    {result.warnings.map((warning, i) => (
                      <ListItem key={i} disablePadding>
                        <ListItemText
                          primary={warning}
                          primaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            ) : (
              <Typography variant="body2">{result.errorMessage || 'Import failed'}</Typography>
            )}
          </Alert>
        )}

        {!result && (
          <>
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="JSON File" />
              <Tab label="Bundle (ZIP)" />
            </Tabs>

            {activeTab === 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Import an adventure from a JSON export file (AdventureExportDto format).
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<FileUpload />}
                  onClick={() => jsonInputRef.current?.click()}
                  disabled={importing}
                  fullWidth
                  sx={{ mb: 1, py: 2 }}
                >
                  {jsonFile ? jsonFile.name : 'Select JSON File'}
                </Button>
                <input
                  ref={jsonInputRef}
                  type="file"
                  accept=".json"
                  style={{ display: 'none' }}
                  onChange={handleJsonFileSelect}
                />
              </Box>
            )}

            {activeTab === 1 && (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Import an adventure bundle (ZIP) including media files, share codes, and QR codes.
                  Max file size: 500 MB.
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<FileUpload />}
                  onClick={() => bundleInputRef.current?.click()}
                  disabled={importing}
                  fullWidth
                  sx={{ mb: 1, py: 2 }}
                >
                  {bundleFile ? bundleFile.name : 'Select ZIP File'}
                </Button>
                <input
                  ref={bundleInputRef}
                  type="file"
                  accept=".zip"
                  style={{ display: 'none' }}
                  onChange={handleBundleFileSelect}
                />
              </Box>
            )}

            {/* Import Options */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Options</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importAsDraft}
                    onChange={(e) => setImportAsDraft(e.target.checked)}
                    disabled={importing}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Import as draft</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={skipTeamMembers}
                    onChange={(e) => setSkipTeamMembers(e.target.checked)}
                    disabled={importing}
                    size="small"
                  />
                }
                label={<Typography variant="body2">Skip team members</Typography>}
              />
              {activeTab === 1 && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={importMediaLibrary}
                        onChange={(e) => setImportMediaLibrary(e.target.checked)}
                        disabled={importing}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Import media library</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={importShareCodes}
                        onChange={(e) => setImportShareCodes(e.target.checked)}
                        disabled={importing}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Import share codes</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={regenerateQRCodes}
                        onChange={(e) => setRegenerateQRCodes(e.target.checked)}
                        disabled={importing}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Regenerate QR codes</Typography>}
                  />
                </>
              )}
            </Box>

            {importing && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption">Importing...</Typography>
                <LinearProgress sx={{ mt: 0.5 }} />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && (
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={importing || !hasFile}
            startIcon={importing ? undefined : <Upload />}
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
